import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ActiveElement,
    ChartData as ChartJsData,
    ChartOptions,
    ChartType as ChartJsType,
    Legend,
    Plugin
} from 'chart.js';
import { ThemeService } from '../../services/theme.service';

export interface ChartDataPoint {
    name: string;
    value: number;
    extra?: unknown;
    [key: string]: unknown;
}

export interface ChartSeries {
    name: string;
    series: ChartDataPoint[];
}

export type ChartData = ChartDataPoint[] | ChartSeries[];
export type ChartType =
    'bar' | 'combo' | 'grouped-bar' | 'stacked-bar' | 'horizontal-stacked-bar' | 'line' | 'pie' | 'doughnut';

interface ChartClickEvent {
    active?: object[];
}

@Component({
    selector: 'ceres-chart',
    template: `
        <div class="ceres-chart" [style.height]="heightValue">
            <canvas
                baseChart
                role="img"
                aria-label="Chart"
                [type]="chartJsType"
                [data]="chartData"
                [options]="options"
                [plugins]="plugins"
                (chartClick)="onChartClick($event)"
            ></canvas>
        </div>
    `,
    styles: `
        :host,
        .ceres-chart {
            display: block;
            width: 100%;
            min-height: 110px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChartComponent implements OnChanges {
    private readonly themeService = inject(ThemeService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    @Input() data: ChartData = [];
    @Input() lineData: ChartSeries[] = [];
    @Input() type: ChartType = 'bar';
    @Input() colors: string[] | { domain: string[] } | string;
    @Input() height: number | string = 300;
    @Input() legend = false;
    @Input() xAxisLabel = '';
    @Input() yAxisLabel = '';
    @Input() showDataLabels = false;
    @Input() yAxisMax: number;
    @Input() valueFormatter: (value: number) => string = (value) => String(value);
    @Output() readonly select = new EventEmitter<ChartDataPoint>();

    chartJsType: ChartJsType = 'bar';
    chartData: ChartJsData<ChartJsType, (number | null)[], string> = { labels: [], datasets: [] };
    options: ChartOptions<ChartJsType> = {};
    readonly plugins: Plugin<ChartJsType>[] = [this.dataLabelsPlugin()];

    private sources: ChartDataPoint[][] = [];
    private readonly defaultColors = ['#5aa454', '#a10a28', '#c7b42c', '#aaaaaa', '#7aa3e5', '#a8385d'];
    private readonly vividColors = ['#647c8a', '#3f51b5', '#2196f3', '#00b862', '#afdf0a', '#a7b61a', '#f3e562'];
    private darkMode = false;

    constructor() {
        this.themeService.darkMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((darkMode) => {
            this.darkMode = darkMode;
            this.options = this.buildOptions();
            this.changeDetectorRef.markForCheck();
        });
    }

    get heightValue(): string {
        return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }

    ngOnChanges(): void {
        this.chartJsType = this.resolveChartType();
        this.chartData = this.buildData();
        this.options = this.buildOptions();
    }

    onChartClick(event: ChartClickEvent): void {
        const active = event.active?.[0] as ActiveElement | undefined;
        const source = active && this.sources[active.datasetIndex]?.[active.index];
        if (source) {
            this.select.emit(source);
        }
    }

    private buildData(): ChartJsData<ChartJsType, (number | null)[], string> {
        if (this.type === 'pie' || this.type === 'doughnut') {
            const data = this.singleData;
            this.sources = [data];
            return {
                labels: data.map((item) => item.name),
                datasets: [
                    {
                        data: data.map((item) => item.value),
                        backgroundColor: data.map((_, index) => this.palette[index % this.palette.length])
                    }
                ]
            };
        }

        if (this.type === 'combo') {
            const categories = this.singleData.map((item) => item.name);
            this.sources = [this.singleData, ...this.lineData.map((entry) => entry.series)];
            return {
                labels: categories,
                datasets: [
                    {
                        type: 'bar',
                        data: this.singleData.map((item) => item.value),
                        backgroundColor: this.singleData.map((_, index) => this.palette[index % this.palette.length]),
                        borderRadius: 4
                    },
                    ...this.lineData.map((entry) => ({
                        type: 'line' as const,
                        label: entry.name,
                        data: categories.map(
                            (name) => entry.series.find((point) => point.name === name)?.value ?? null
                        ),
                        borderColor: '#01579b',
                        backgroundColor: '#01579b',
                        pointRadius: 0,
                        tension: 0
                    }))
                ]
            };
        }

        if (this.type === 'line') {
            const categories = this.categoryNames;
            this.sources = this.multiData.map((entry) =>
                categories.map((name) => entry.series.find((point) => point.name === name) ?? { name, value: 0 })
            );
            return {
                labels: categories,
                datasets: this.multiData.map((entry, index) => ({
                    label: entry.name,
                    data: categories.map((name) => entry.series.find((point) => point.name === name)?.value ?? null),
                    borderColor: this.palette[index % this.palette.length],
                    backgroundColor: this.palette[index % this.palette.length],
                    pointRadius: 3,
                    tension: 0
                }))
            };
        }

        if (this.type === 'grouped-bar' || this.type === 'stacked-bar' || this.type === 'horizontal-stacked-bar') {
            const seriesNames = [...new Set(this.multiData.flatMap((entry) => entry.series.map((item) => item.name)))];
            this.sources = seriesNames.map((seriesName) =>
                this.multiData.map((entry) => {
                    const item = entry.series.find((point) => point.name === seriesName);
                    return item ? { ...item, name: seriesName, series: entry.name } : { name: seriesName, value: 0 };
                })
            );
            return {
                labels: this.multiData.map((entry) => entry.name),
                datasets: seriesNames.map((seriesName, index) => ({
                    label: seriesName,
                    data: this.multiData.map(
                        (entry) => entry.series.find((point) => point.name === seriesName)?.value ?? 0
                    ),
                    backgroundColor: this.palette[index % this.palette.length],
                    borderRadius: 4
                }))
            };
        }

        this.sources = [this.singleData];
        return {
            labels: this.singleData.map((item) => item.name),
            datasets: [
                {
                    data: this.singleData.map((item) => item.value),
                    backgroundColor: this.singleData.map((_, index) => this.palette[index % this.palette.length]),
                    borderRadius: 4
                }
            ]
        };
    }

    private buildOptions(): ChartOptions<ChartJsType> {
        const horizontal = this.type === 'horizontal-stacked-bar';
        const stacked = this.type === 'stacked-bar' || horizontal;
        const circular = this.type === 'pie' || this.type === 'doughnut';
        const colors = this.themeColors;
        const hasCategoryLegend = (this.type === 'bar' || this.type === 'combo') && this.legend;
        const integerData = this.hasIntegerData;
        const legendLabels = {
            ...(this.darkMode ? { color: colors.text } : {}),
            ...(hasCategoryLegend
                ? {
                      generateLabels: (chart) => {
                          const categories = (chart.data.labels ?? []).map((label, index) => ({
                              text: String(label),
                              fillStyle: this.categoryColor(chart, index),
                              strokeStyle: this.categoryColor(chart, index),
                              hidden: !chart.getDataVisibility(index),
                              datasetIndex: 0,
                              index
                          }));
                          if (this.type !== 'combo') {
                              return categories;
                          }

                          const lineItems = Legend.defaults.labels
                              .generateLabels(chart)
                              .filter((item) => chart.data.datasets[item.datasetIndex ?? -1]?.type === 'line');
                          return [...categories, ...lineItems];
                      }
                  }
                : {})
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: horizontal ? 'y' : 'x',
            animation: { duration: 500 },
            plugins: {
                legend: {
                    display: this.legend,
                    position: 'bottom',
                    labels: Object.keys(legendLabels).length ? legendLabels : undefined,
                    ...(hasCategoryLegend
                        ? {
                              onClick: (_event, legendItem, legend) => {
                                  if (legendItem.datasetIndex === 0 && legendItem.index !== undefined) {
                                      legend.chart.toggleDataVisibility(legendItem.index);
                                  } else if (legendItem.datasetIndex !== undefined) {
                                      if (legend.chart.isDatasetVisible(legendItem.datasetIndex)) {
                                          legend.chart.hide(legendItem.datasetIndex);
                                      } else {
                                          legend.chart.show(legendItem.datasetIndex);
                                      }
                                  }
                                  legend.chart.update();
                              }
                          }
                        : {})
                },
                tooltip: {
                    callbacks: {
                        title: (contexts) => contexts[0]?.label ?? '',
                        label: (context) => {
                            const prefix = context.dataset.label ? `${context.dataset.label}: ` : '';
                            return `${prefix}${this.valueFormatter(Number(context.raw))}`;
                        }
                    }
                }
            },
            scales: circular
                ? undefined
                : {
                      x: {
                          stacked,
                          title: {
                              display: Boolean(horizontal ? this.yAxisLabel : this.xAxisLabel),
                              text: horizontal ? this.yAxisLabel : this.xAxisLabel,
                              ...(this.darkMode ? { color: colors.text } : {})
                          },
                          ticks: horizontal
                              ? {
                                    callback: (value) => this.valueFormatter(Number(value)),
                                    precision: integerData ? 0 : undefined,
                                    ...(this.darkMode ? { color: colors.text } : {})
                                }
                              : {
                                    autoSkip: true,
                                    maxRotation: 90,
                                    minRotation: 0,
                                    callback: (_value, index) =>
                                        this.truncateLabel(String(this.chartData.labels?.[index] ?? '')),
                                    ...(this.darkMode ? { color: colors.text } : {})
                                },
                          grid: this.darkMode ? { color: colors.grid } : undefined,
                          border: this.darkMode ? { color: colors.grid } : undefined
                      },
                      y: {
                          stacked,
                          beginAtZero: true,
                          max: horizontal ? undefined : this.yAxisMax,
                          title: {
                              display: Boolean(horizontal ? this.xAxisLabel : this.yAxisLabel),
                              text: horizontal ? this.xAxisLabel : this.yAxisLabel,
                              ...(this.darkMode ? { color: colors.text } : {})
                          },
                          ticks: horizontal
                              ? {
                                    autoSkip: true,
                                    callback: (_value, index) =>
                                        this.truncateLabel(String(this.chartData.labels?.[index] ?? '')),
                                    ...(this.darkMode ? { color: colors.text } : {})
                                }
                              : {
                                    callback: (value) => this.valueFormatter(Number(value)),
                                    precision: integerData ? 0 : undefined,
                                    ...(this.darkMode ? { color: colors.text } : {})
                                },
                          grid: this.darkMode ? { color: colors.grid } : undefined,
                          border: this.darkMode ? { color: colors.grid } : undefined
                      }
                  }
        };
    }

    private dataLabelsPlugin(): Plugin<ChartJsType> {
        return {
            id: 'ceres-data-labels',
            afterDatasetsDraw: (chart) => {
                if (!this.showDataLabels) {
                    return;
                }

                const { ctx } = chart;
                ctx.save();
                ctx.fillStyle = this.darkMode ? this.themeColors.dataLabel : '#333';
                ctx.font = '11px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';

                chart.data.datasets.forEach((dataset, datasetIndex) => {
                    const meta = chart.getDatasetMeta(datasetIndex);
                    meta.data.forEach((element, index) => {
                        const value = Number(dataset.data[index]);
                        if (!Number.isFinite(value)) {
                            return;
                        }
                        const position = element.tooltipPosition(false);
                        ctx.fillText(this.valueFormatter(value), position.x, position.y - 4);
                    });
                });
                ctx.restore();
            }
        };
    }

    private resolveChartType(): ChartJsType {
        if (this.type === 'pie' || this.type === 'doughnut' || this.type === 'line') {
            return this.type;
        }
        return 'bar';
    }

    private get singleData(): ChartDataPoint[] {
        return this.data.filter((item): item is ChartDataPoint => 'value' in item);
    }

    private get multiData(): ChartSeries[] {
        return this.data.filter((item): item is ChartSeries => 'series' in item);
    }

    private get categoryNames(): string[] {
        return [...new Set(this.multiData.flatMap((entry) => entry.series.map((item) => item.name)))];
    }

    private get palette(): string[] {
        if (Array.isArray(this.colors) && this.colors.length) {
            return this.colors;
        }
        if (this.colors && typeof this.colors === 'object' && 'domain' in this.colors && this.colors.domain.length) {
            return this.colors.domain;
        }
        return this.colors === 'vivid' ? this.vividColors : this.defaultColors;
    }

    private get hasIntegerData(): boolean {
        const values = this.chartData.datasets
            .flatMap((dataset) => dataset.data)
            .filter((value): value is number => typeof value === 'number');
        return values.every(Number.isInteger);
    }

    private get themeColors(): { text: string; grid: string; dataLabel: string } {
        return {
            text: '#ccccd4',
            grid: 'rgba(255,255,255,0.15)',
            dataLabel: '#ffffff'
        };
    }

    private truncateLabel(label: string): string {
        const limit = 16;
        return label.length > limit ? `${label.slice(0, limit - 1)}…` : label;
    }

    private categoryColor(chart: { data: { datasets: { backgroundColor?: unknown }[] } }, index: number): string {
        const background = chart.data.datasets[0]?.backgroundColor;
        return Array.isArray(background)
            ? String(background[index] ?? this.palette[index % this.palette.length])
            : String(background ?? this.palette[index % this.palette.length]);
    }
}
